import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { CostCard, money, type ServerCost } from '../components/CostCard';
import { ConfigEditor } from '../components/ConfigEditor';
import { EnvSection } from '../components/EnvEditor';
import { IdentityCard, type ServerIdentity } from '../components/IdentityCard';
import { TimeSeries } from '../components/TimeSeries';
import { Card, Empty, EntityChip, Meter, SortHeader, StatTile, StatusPill } from '../components/ui';
import { ago, bytes, duration, percent, ratio } from '../lib/format';
import { api } from '../lib/api';
import { useApi } from '../lib/useApi';
import { useSort } from '../lib/useSort';

interface ServerRow {
  id: string;
  name: string;
  hostname: string;
  ipAddress: string | null;
  os: string | null;
  firewall: string | null;
  osVersion: string | null;
  kernel: string | null;
  dockerVersion: string | null;
  cpuCores: number | null;
  memoryBytes: string | null;
  status: string;
  lastSeenAt: string | null;
  uptimeSeconds: string | null;
  sshEnabled: boolean;
  lastError: string | null;
  location: string | null;
  machineHostname: string | null;
  machineFqdn: string | null;
  notes: string | null;
  monthlyCost: string | null;
  costCurrency: string | null;
  costNote: string | null;
  costUpdatedAt: string | null;
  costUpdatedBy: string | null;
  client: { id: string; name: string; code: string } | null;
  deployments: Array<{
    id: string;
    name: string;
    version: string | null;
    product: { key: string; name: string };
    /** Null for shared `-common` infrastructure. Declaring it non-null let a
     *  runtime null reach `d.client.code` and blank the whole page. */
    client: { code: string; name: string } | null;
  }>;
  latest: {
    cpuPercent: string | null;
    memUsedBytes: string | null;
    memTotalBytes: string | null;
    diskUsedBytes: string | null;
    diskTotalBytes: string | null;
  } | null;
}

/**
 * Who has workloads on this server, and what is deployed — two labelled chip
 * groups rather than one undifferentiated pile.
 *
 * The owning client is marked "owner"; everyone else is simply listed. The
 * earlier version tagged the *others* as "guest", which is the same fact stated
 * so that it has to be decoded — naming the owner is positive information, and
 * the absence of a tag then makes no claim at all.
 */
function ServerChips({
  deployments,
  owner,
}: {
  deployments: ServerRow['deployments'];
  owner: ServerRow['client'];
}) {
  const counts = new Map<string, { name: string; n: number }>();
  for (const d of deployments) {
    // Shared `-common` stacks are skipped entirely here. This group answers
    // "whose workloads are on this box", and infrastructure belonging to nobody
    // is not an answer to it — listing it as a pseudo-client made the host look
    // like it carried one more tenant than it does.
    if (!d.client) continue;
    const c = counts.get(d.client.code) ?? { name: d.client.name, n: 0 };
    c.n += 1;
    counts.set(d.client.code, c);
  }
  const clients = [...counts.entries()].sort((a, b) => b[1].n - a[1].n);
  const products = [...deployments].sort((a, b) => a.product.name.localeCompare(b.product.name));

  return (
    <>
      <div className="chip-group">
        <h3>Clients</h3>
        <ul className="echip-row tight">
          {clients.map(([code, { name, n }]) => (
            <li key={code}>
              <EntityChip
                type="client"
                label={name}
                meta={n}
                tag={owner && owner.code === code ? 'owner' : undefined}
                title={`${name}: ${n} deployment${n === 1 ? '' : 's'} on this server`}
              />
            </li>
          ))}
          {clients.length === 0 && <li className="muted small">No deployments recorded.</li>}
        </ul>
      </div>

      <div className="chip-group">
        <h3>Products</h3>
        <ul className="echip-row tight">
          {products.map((d) => (
            <li key={d.id}>
              <EntityChip
                type="product"
                label={d.product.name}
                meta={d.version ?? undefined}
                tag={d.client?.code ?? "COMMON"}
                title={`${d.product.name} for ${d.client?.name ?? "all clients (shared)"} — deployment "${d.name}"`}
              />
            </li>
          ))}
          {products.length === 0 && <li className="muted small">None.</li>}
        </ul>
      </div>
    </>
  );
}

interface Capabilities {
  canEditEnv: boolean;
  canEditInventory: boolean;
  canManageUsers: boolean;
  canTouchProduction: boolean;
  canTriggerCollection: boolean;
}

interface CostSummary {
  totals: Array<{ currency: string; monthly: string; annual: string }>;
  priced: number;
  unpriced: number;
}

/**
 * Re-reads one host's installed stacks and containers, on demand.
 *
 * Collection is a 60-second poll, so for up to a minute after deploying or
 * stopping a stack this page is stale — which is exactly when someone comes
 * here, having just changed something, to see whether it worked. This asks that
 * one host now, and only that host: a refresh of CA-001 should not open an SSH
 * session to the other four.
 *
 * Disabled rather than hidden when the account cannot trigger a collection, so
 * the control does not materialise and vanish between roles, and disabled with a
 * reason when the server has no SSH configured — there is nothing to ask.
 */
function RefreshButton({
  serverId,
  sshEnabled,
  canTrigger,
  onDone,
}: {
  serverId: string;
  sshEnabled: boolean;
  canTrigger: boolean;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setNote(null);
    try {
      const r = await api<{ collected: number; failed: number; skipped: number }>(
        `/servers/${serverId}/collect`,
        { method: 'POST' },
      );
      if (r.failed > 0) setNote('The host did not answer — nothing changed.');
      else if (r.skipped > 0) setNote('SSH is not configured for this server.');
      // Reloaded even on a failed run: the attempt itself is recorded, and the
      // page shows collection runs.
      onDone();
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Refresh failed');
    } finally {
      setBusy(false);
    }
  }

  const why = !canTrigger
    ? 'Refreshing runs a command on a production host, so it needs operator rights or above'
    : !sshEnabled
      ? 'SSH is not configured for this server'
      : 'Re-read the deployments and containers installed on this host';

  return (
    <span className="refresh-control">
      <button
        type="button"
        className="ghost"
        disabled={busy || !canTrigger || !sshEnabled}
        onClick={run}
        title={why}
      >
        {busy ? 'Refreshing…' : 'Refresh'}
      </button>
      {note && <span className="form-error small">{note}</span>}
    </span>
  );
}

export function Servers() {
  const { data, error, loading, reload } = useApi<ServerRow[]>('/servers');
  const { data: costs } = useApi<CostSummary>('/costs');

  // Asked for rather than inferred from the role, for the same reason the detail
  // page does: this app has been wrong about SUPERADMIN once already.
  const [caps, setCaps] = useState<Capabilities | null>(null);
  useEffect(() => {
    api<Capabilities>('/capabilities')
      .then(setCaps)
      .catch(() => setCaps(null));
  }, []);

  if (loading && !data) return <p className="empty">Loading servers…</p>;
  if (error && !data) return <p className="form-error">{error}</p>;

  return (
    <>
      <div className="page-head">
        <h1>Servers</h1>
      </div>

      {costs && (costs.totals.length > 0 || costs.unpriced > 0) && (
        <Card title="Monthly cost">
          <div className="cost-totals">
            {/* One figure per currency, never a converted total: PlatformIQ has no
                FX rate, and a single number produced from a guessed one would look
                authoritative while being wrong. */}
            {costs.totals.map((t) => (
              <span key={t.currency} className="cost-total">
                <span className="amount">{money(t.monthly, t.currency)}</span>
                <span className="per">/ month · {money(t.annual, t.currency)} / year</span>
              </span>
            ))}
            {costs.totals.length === 0 && <span className="muted">No costs recorded yet.</span>}
          </div>
          {costs.unpriced > 0 && (
            <p className="muted small">
              {costs.priced} of {costs.priced + costs.unpriced} servers have a cost recorded — these
              totals exclude {costs.unpriced}.
            </p>
          )}
        </Card>
      )}

      <div className="grid-cards">
        {(data ?? []).map((s) => (
          <Card
            key={s.id}
            title={<Link to={`/servers/${s.id}`}>{s.name}</Link>}
            actions={
              <>
                <RefreshButton
                  serverId={s.id}
                  sshEnabled={s.sshEnabled}
                  canTrigger={caps?.canTriggerCollection ?? false}
                  onDone={reload}
                />
                <StatusPill status={s.sshEnabled ? s.status : 'UNKNOWN'} />
              </>
            }
          >

            <dl className="facts">
              {/* The exact OS version, collected rather than declared — the whole
                  estate is "Ubuntu 24.x" on paper and four patch levels in fact. */}
              <dt>OS</dt>
              <dd>{s.osVersion ? `${s.os ?? ''} ${s.osVersion}`.trim() : '—'}</dd>
              <dt>Kernel</dt>
              <dd className="mono">{s.kernel ?? '—'}</dd>
              <dt>Docker</dt>
              <dd className="mono">{s.dockerVersion ?? '—'}</dd>
              <dt>Capacity</dt>
              <dd>{s.cpuCores ? `${s.cpuCores} vCPU` : '—'} · {bytes(s.memoryBytes)}</dd>
              <dt>Uptime</dt>
              <dd>{duration(s.uptimeSeconds)}</dd>
              {/* Location leads the identity facts: on a list of five boxes it is
                  the thing that tells them apart at a glance, where the IP does
                  not until you have memorised five of them. */}
              <dt>Location</dt>
              <dd>{s.location ?? <span className="muted">not recorded</span>}</dd>
              <dt>Address</dt>
              <dd className="mono">{s.ipAddress ?? s.hostname}</dd>
              <dt>Last seen</dt>
              <dd title={s.lastSeenAt ?? ''}>{ago(s.lastSeenAt)}</dd>
            </dl>

            {s.latest && (
              <div className="meters">
                <label>CPU<Meter value={s.latest.cpuPercent === null ? null : Number(s.latest.cpuPercent)} label="CPU" /></label>
                <label>Memory<Meter value={ratio(s.latest.memUsedBytes, s.latest.memTotalBytes)} label="Memory" /></label>
                <label>Disk<Meter value={ratio(s.latest.diskUsedBytes, s.latest.diskTotalBytes)} label="Disk" /></label>
              </div>
            )}

            {s.monthlyCost !== null && (
              <p className="cost-inline">
                {money(s.monthlyCost, s.costCurrency)} <span className="muted">/ month</span>
              </p>
            )}

            {!s.sshEnabled && <p className="notice">Not monitored — no collector credentials.</p>}
            {s.lastError && <p className="notice notice-bad">{s.lastError}</p>}

            <ServerChips deployments={s.deployments} owner={s.client} />
          </Card>
        ))}
      </div>
    </>
  );
}

/** A deployment as the server detail page needs it — more than the list card shows. */
interface ServerDeployment {
  id: string;
  name: string;
  version: string | null;
  composePath: string | null;
  isDeclared: boolean;
  needsReview: boolean;
  containers: number;
  product: { key: string; name: string; isInfrastructure: boolean };
  /** Null for shared `-common` infrastructure, which deliberately has no client. */
  client: { id: string; code: string; name: string } | null;
}

interface ServerDetailData {
  server: ServerRow & {
    deployments: ServerDeployment[];
    formerDeployments: number;
    containers: Array<ContainerRow>;
    endpoints: EndpointRow[];
  };
  metrics: {
    bucketSeconds: number;
    points: number;
    oldest: string | null;
    series: Array<{
      t: string;
      cpuAvg: number;
      cpuMax: number;
      memAvg: number;
      memMax: number;
      memTotal: number;
      diskUsed: number | null;
      diskTotal: number | null;
      samples: number;
    }>;
  };
  disks: Array<{ mountPoint: string; filesystem: string | null; sizeBytes: string; usedBytes: string }>;
  runs: Array<{ id: string; startedAt: string; status: string; durationMs: number | null; error: string | null }>;
}

interface ContainerRow {
  id: string;
  name: string;
  image: string;
  imageTag: string | null;
  version: string | null;
  state: string;
  health: string;
  restartCount: number;
  ports: string | null;
}

interface EndpointRow {
  id: string;
  kind: 'PUBLIC_HTTPS' | 'HOST_PORT' | 'TUNNEL' | 'INTERNAL';
  url: string | null;
  hostname: string | null;
  bindAddress: string | null;
  port: number | null;
  protocol: string | null;
  targetName: string | null;
  targetPort: number | null;
  isPublic: boolean;
  source: string;
  deployment: { name: string; product: { name: string } } | null;
}

/**
 * The containers on one server.
 *
 * Its own component so it can call useSort: ServerDetail returns early while
 * loading, and a hook after a conditional return is a hooks-order violation that
 * React catches at runtime rather than at compile time.
 */
function ContainersTable({ containers }: { containers: ContainerRow[] }) {
  const { sorted, state: sort } = useSort<ContainerRow>(
    containers,
    {
      name: (c) => c.name,
      image: (c) => c.image,
      version: (c) => c.version ?? c.imageTag,
      state: (c) => c.state,
      health: (c) => c.health,
      restarts: (c) => c.restartCount,
    },
    'name',
  );

  return (
    <Card title={`Containers (${containers.length})`}>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <SortHeader label="Name" sortKey="name" state={sort} />
              <SortHeader label="Image" sortKey="image" state={sort} />
              <SortHeader label="Version" sortKey="version" state={sort} />
              <SortHeader label="State" sortKey="state" state={sort} />
              <SortHeader label="Health" sortKey="health" state={sort} />
              <SortHeader label="Restarts" sortKey="restarts" state={sort} numeric />
              <th>Ports</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => (
              <tr key={c.id}>
                <td className="mono">{c.name}</td>
                <td className="mono muted">{c.image}</td>
                <td>{c.version ?? <span className="muted">{c.imageTag ?? 'untagged'}</span>}</td>
                <td><StatusPill status={c.state} label={c.state.toLowerCase()} /></td>
                <td>
                  {c.health === 'NONE'
                    ? <span className="muted">none declared</span>
                    : <StatusPill status={c.health} label={c.health.toLowerCase()} />}
                </td>
                <td className="num">{c.restartCount}</td>
                <td className="mono muted small">{c.ports ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/**
 * What can reach this server from outside, and from where.
 *
 * The "from where" half is the honest part. Nothing in this estate restricts
 * source IPs — no Caddy `remote_ip` matcher, no rules the collector can see — so
 * the answer is "any IP", and the page says that rather than leaving the column
 * blank and letting it read as "restricted".
 *
 * Firewall RULES need root; the collector account has no sudo, so what is shown
 * is whether a firewall is running at all. That is deliberately not dressed up as
 * more than it is.
 */
function Exposure({
  server,
  endpoints,
}: {
  server: { firewall: string | null };
  endpoints: EndpointRow[];
}) {
  const external = endpoints.filter((e) => e.isPublic);
  const fw = server.firewall ?? 'unknown';
  const noFirewall = fw === 'none';

  const label = (e: EndpointRow) => {
    if (e.url) return e.url;
    if (e.kind === 'TUNNEL') return `Cloudflare tunnel → ${e.targetName}:${e.targetPort ?? ''}`;
    return `${e.bindAddress ?? '0.0.0.0'}:${e.port}/${e.protocol ?? 'tcp'}`;
  };

  return (
    <Card
      title="Exposure"
      actions={
        <span className={`pill ${noFirewall ? 'pill-down' : 'pill-warn'}`}>
          <span aria-hidden="true">{noFirewall ? '■' : '▲'}</span>
          {noFirewall ? 'No host firewall' : `Firewall: ${fw}`}
        </span>
      }
    >
      {noFirewall ? (
        <p className="notice notice-bad">
          No host firewall is running (ufw, nftables and firewalld are all inactive). Every port
          published on <code>0.0.0.0</code> below is reachable from the whole internet.
        </p>
      ) : (
        <p className="notice">
          <strong>{fw}</strong> is running, but its rules are not collected — reading them needs
          root and the collector account has no sudo. Note also that Docker&apos;s published ports
          bypass ufw&apos;s INPUT rules entirely, so a ufw allowlist does not restrict the ports
          below unless matching rules exist in the <code>DOCKER-USER</code> chain.
        </p>
      )}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr><th>Reachable at</th><th>Product</th><th>Goes to</th><th>Allowed source IPs</th></tr>
          </thead>
          <tbody>
            {external.map((e) => (
              <tr key={e.id}>
                <td className="mono">
                  {e.url ? <a href={e.url} target="_blank" rel="noreferrer noopener">{label(e)}</a> : label(e)}
                </td>
                <td>{e.deployment?.product.name ?? <span className="muted">—</span>}</td>
                <td className="mono muted small">
                  {e.targetName ? `${e.targetName}${e.targetPort ? `:${e.targetPort}` : ''}` : '—'}
                </td>
                <td>
                  <span className="pill pill-down">
                    <span aria-hidden="true">■</span>
                    Any
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {external.length === 0 && <Empty>Nothing on this server is reachable from outside.</Empty>}

      <p className="muted small">
        Source-IP restriction is read from Caddy&apos;s <code>remote_ip</code> matchers. None are
        configured on any host, so every entry above accepts connections from any address. Host
        firewall rules are not yet collected — see the note above.
      </p>
    </Card>
  );
}

const RANGES = [
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 24 * 7 },
  { label: '30 days', hours: 24 * 30 },
] as const;

/** "2 hours", "30 minutes" — the width of one aggregated point. */
function bucketLabel(seconds: number): string {
  if (seconds < 3600) return `${Math.round(seconds / 60)}-minute`;
  return `${Math.round(seconds / 3600)}-hour`;
}

/**
 * What is deployed on this server, as a table with the client on every row.
 *
 * This replaced two chip groups — one of clients, one of products — which read
 * nicely but could not say WHICH client each product belonged to. On a shared
 * host that is the whole question: 23.19.228.49 carries three clients, and
 * "ABC, ADQ" beside "BusinessIQ, StockIQ, PlatformIQ" leaves the reader to guess
 * the pairing, with no way to be right.
 *
 * Sorted by client first so each client's footprint reads as a block, and the
 * owning client is marked rather than the others being tagged as guests.
 */
function DeploymentsHere({
  deployments,
  owner,
  former,
}: {
  deployments: ServerDeployment[];
  owner: ServerRow['client'];
  /** Stacks with no containers left. Transient — the next refresh deletes them. */
  former: number;
}) {
  const { sorted, state: sort } = useSort<ServerDeployment>(
    deployments,
    {
      // Tilde sorts after letters, so COMMON rows settle at the end rather than
    // interleaving with real clients.
    client: (d) => d.client?.code ?? "~COMMON",
      product: (d) => d.product.name,
      version: (d) => d.version,
      stack: (d) => d.name,
      containers: (d) => d.containers,
    },
    'client',
  );

  // Shared stacks are not a client, so they do not inflate the client count.
  const clients = new Set(deployments.filter((d) => d.client).map((d) => d.client!.code));
  const live = deployments.reduce((n, d) => n + d.containers, 0);

  return (
    <Card
      title="Deployed here"
      actions={
        <span className="muted small">
          {deployments.length} deployment{deployments.length === 1 ? '' : 's'} ·{' '}
          {clients.size} client{clients.size === 1 ? '' : 's'} · {live} container
          {live === 1 ? '' : 's'}
        </span>
      }
    >
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <SortHeader label="Client" sortKey="client" state={sort} />
              <SortHeader label="Product" sortKey="product" state={sort} />
              <SortHeader label="Version" sortKey="version" state={sort} />
              <SortHeader label="Deployment" sortKey="stack" state={sort} />
              <SortHeader label="Containers" sortKey="containers" state={sort} numeric />
            </tr>
          </thead>
          <tbody>
            {sorted.map((d) => (
              <tr key={d.id}>
                <td>
                  {d.client ? (
                    <Link to={`/clients/${d.client.id}`}>
                      <EntityChip
                        type="client"
                        label={d.client.code}
                        tag={owner && owner.code === d.client.code ? 'owner' : undefined}
                        title={d.client.name}
                      />
                    </Link>
                  ) : (
                    // COMMON is a real value, not a blank: a `-common` stack
                    // genuinely has no client, and an empty cell would read as
                    // missing data rather than as the answer.
                    //
                    // Set in the client column's own style rather than as a
                    // client chip, because it is deliberately NOT one — a chip
                    // would invite clicking through to a client that does not
                    // exist.
                    <span className="tag tag-common" title="Shared by every client on this host">
                      COMMON
                    </span>
                  )}
                </td>
                <td>
                  <EntityChip type="product" label={d.product.name} />
                  {d.product.isInfrastructure && <span className="tag">infra</span>}
                </td>
                <td>
                  {d.version ? (
                    <span className="ver">{d.version}</span>
                  ) : (
                    // Never invented. An untagged or :local image has no version,
                    // and "latest" is a tag rather than a release.
                    <span className="muted small">not versioned</span>
                  )}
                </td>
                <td>
                  <code className="stack-name" title={d.composePath ?? undefined}>{d.name}</code>
                  {d.needsReview && (
                    <span
                      className="tag tag-warn"
                      title="Discovered by the collector; the client was inherited from the server owner rather than stated by the stack name"
                    >
                      needs review
                    </span>
                  )}
                </td>
                <td className="num">{d.containers}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {deployments.length === 0 && <Empty>Nothing deployed here yet.</Empty>}
      {former > 0 && (
        // Said out loud rather than silently filtered, but it should now be rare
        // and short-lived: the collector deletes a stack once its last container
        // is gone, so this only shows in the window between a stack stopping and
        // the next refresh noticing.
        <p className="muted small">
          {former} stack{former === 1 ? '' : 's'} here {former === 1 ? 'has' : 'have'} no containers
          left and {former === 1 ? 'is' : 'are'} hidden — Refresh removes {former === 1 ? 'it' : 'them'}.
        </p>
      )}
    </Card>
  );
}

export function ServerDetail() {
  const { id } = useParams<{ id: string }>();
  const [hours, setHours] = useState<number>(24);
  const { data, error, loading, reload } = useApi<ServerDetailData>(
    id ? `/servers/${id}?hours=${hours}` : null,
  );
  // What this account may do, answered by the server rather than worked out here.
  // The page used to test `role === 'ADMIN' || role === 'OPERATOR'` itself, which
  // silently hid every edit button from SUPERADMIN the day that role was added.
  // Asking also beats inferring for the env editor, which is gated on more than
  // the role — otherwise the card renders and can only ever return 403.
  const [caps, setCaps] = useState<Capabilities | null>(null);
  useEffect(() => {
    api<Capabilities>('/capabilities')
      .then(setCaps)
      .catch(() => setCaps(null));
  }, []);

  if (loading && !data) return <p className="empty">Loading…</p>;
  if (error && !data) return <p className="form-error">{error}</p>;
  if (!data) return null;

  const { server, metrics, disks, runs } = data;
  const times = metrics.series.map((m) => new Date(m.t));
  const latest = metrics.series[metrics.series.length - 1];

  // Retention is 30 days, and collection started when PlatformIQ was deployed —
  // so asking for 30 days does not mean 30 days exist. Say which, rather than
  // drawing a mostly-empty axis and letting it look like an outage.
  const oldest = metrics.oldest ? new Date(metrics.oldest) : null;
  const coveredHours = oldest ? (Date.now() - oldest.getTime()) / 3_600_000 : 0;
  const shortOfWindow = oldest !== null && coveredHours < hours * 0.9;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{server.name}</h1>
          <p className="mono muted">{server.hostname}</p>
        </div>
        <div className="head-actions">
          <RefreshButton
            serverId={server.id}
            sshEnabled={server.sshEnabled}
            canTrigger={caps?.canTriggerCollection ?? false}
            onDone={reload}
          />
          <StatusPill status={server.status} />
        </div>
      </div>

      <DeploymentsHere deployments={server.deployments} owner={server.client} former={server.formerDeployments} />

      <div className="tiles">
        <StatTile label="Operating system" value={server.osVersion ?? '—'} sub={server.os ?? ''} />
        <StatTile label="Kernel" value={<span className="mono">{server.kernel ?? '—'}</span>} />
        <StatTile label="Docker" value={<span className="mono">{server.dockerVersion ?? '—'}</span>} />
        <StatTile label="Uptime" value={duration(server.uptimeSeconds)} />
      </div>

      <div className="range-bar">
        <span className="muted small">Range</span>
        {RANGES.map((r) => (
          <button
            key={r.hours}
            type="button"
            className={hours === r.hours ? '' : 'ghost'}
            onClick={() => setHours(r.hours)}
          >
            {r.label}
          </button>
        ))}
        <span className="muted small">
          {metrics.points} points · {bucketLabel(metrics.bucketSeconds)} averages
        </span>
      </div>

      {shortOfWindow && (
        <p className="notice">
          Only {coveredHours < 48 ? `${Math.round(coveredHours)} hours` : `${Math.round(coveredHours / 24)} days`} of
          history exists so far — collection began {ago(oldest)}. Raw samples are kept for 30 days,
          so this window fills in as it goes.
        </p>
      )}

      {/* One measure per chart, and one y-axis each. CPU and memory share no scale,
          and a second axis would let the author choose where the lines cross —
          which means the reader learns nothing from where they do.

          Average AND peak per bucket, because at 30 days one point is two hours:
          a host pinned at 100% for ten minutes averages to a calm 8%, and the
          average alone would hide the only thing worth seeing. */}
      <div className="chart-grid">
        <TimeSeries
          title="CPU"
          max={100}
          unit="%"
          series={[
            {
              label: 'Peak',
              color: 'var(--series-2)',
              points: metrics.series.map((m, i) => ({ t: times[i], v: m.cpuMax })),
            },
            {
              label: 'Average',
              color: 'var(--series-1)',
              points: metrics.series.map((m, i) => ({ t: times[i], v: m.cpuAvg })),
            },
          ]}
        />
        <TimeSeries
          title="Memory used"
          format={(v) => bytes(v)}
          max={latest?.memTotal || undefined}
          series={[
            {
              label: 'Peak',
              color: 'var(--series-2)',
              points: metrics.series.map((m, i) => ({ t: times[i], v: m.memMax })),
            },
            {
              label: 'Average',
              color: 'var(--series-1)',
              points: metrics.series.map((m, i) => ({ t: times[i], v: m.memAvg })),
            },
          ]}
        />
        <TimeSeries
          title="Disk used"
          format={(v) => bytes(v)}
          max={latest?.diskTotal ?? undefined}
          series={[
            {
              label: 'Disk',
              color: 'var(--series-3)',
              points: metrics.series.map((m, i) => ({ t: times[i], v: m.diskUsed })),
            },
          ]}
        />
      </div>

      {/* Endpoints come back nested under the server object, not at the top level — the
          Prisma include hangs off the server query. */}
      <IdentityCard
        server={server as unknown as ServerIdentity}
        canEdit={caps?.canEditInventory === true}
        onSaved={reload}
      />

      <CostCard
        serverId={server.id}
        cost={server as ServerCost}
        canEdit={caps?.canEditInventory === true}
        onSaved={reload}
      />

      <EnvSection serverId={server.id} canEdit={caps?.canEditEnv === true} />

      <ConfigEditor serverId={server.id} canEdit={caps?.canEditEnv === true} />

      <Exposure server={server} endpoints={server.endpoints ?? []} />

      <Card title="Filesystems">
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr><th>Mount</th><th>Filesystem</th><th>Used</th><th>Size</th><th>Usage</th></tr>
            </thead>
            <tbody>
              {disks.map((d) => (
                <tr key={d.mountPoint}>
                  <td className="mono">{d.mountPoint}</td>
                  <td className="mono muted">{d.filesystem ?? '—'}</td>
                  <td className="num">{bytes(d.usedBytes)}</td>
                  <td className="num">{bytes(d.sizeBytes)}</td>
                  <td><Meter value={ratio(d.usedBytes, d.sizeBytes)} label={d.mountPoint} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ContainersTable containers={server.containers} />

      <Card title="Recent collections">
        <ul className="runs">
          {runs.map((r) => (
            <li key={r.id}>
              <StatusPill status={r.status === 'SUCCESS' ? 'UP' : 'UNREACHABLE'} label={r.status.toLowerCase()} />
              <span title={r.startedAt}>{ago(r.startedAt)}</span>
              <span className="muted">{r.durationMs ? `${r.durationMs} ms` : ''}</span>
              {r.error && <span className="notice-bad">{r.error}</span>}
            </li>
          ))}
          {runs.length === 0 && <Empty>No collection runs recorded.</Empty>}
        </ul>
      </Card>

      {latest && (
        <p className="muted small">
          Most recent bucket: CPU {percent(latest.cpuAvg, 1)} average, {percent(latest.cpuMax, 1)} peak ·
          memory {bytes(latest.memAvg)} of {bytes(latest.memTotal)}.
        </p>
      )}
    </>
  );
}
